import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "zustand";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import GoBack from "../../../assets/GoBackApp.svg";
import { PATH_LIST } from "shared/lib/react-router";
import { PersonalInfoEditCard } from "../components/personal-info-edit-card";
import { sessionModel } from "entities/session";
import { accountStore } from "entities/account";
import {
  startRegistration,
  browserSupportsWebAuthn,
  startAuthentication,
} from "@simplewebauthn/browser";
import Modal from "widgets/auth/modal";
import "./style.scss";
import { ResponseCodes, sircapApi } from "shared/api/sircap";
import { useBlockScrollbar } from "hooks/useBlockScrollbar";
import { Capacitor } from "@capacitor/core";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";

export const SecurityPage: React.FC = () => {
  const { t } = useTranslation("app");
  const { t: err } = useTranslation("errors");
  const { t: o } = useTranslation("onboarding-auth");
  const {
    user,
    entity,
    biometric,
    biometrics,
    inactivePinCodeModal,
    setInactivePinCodeModal,
  } = useStore(sessionModel.sessionStore);
  const [bioModal, setBioModal] = useState(false);
  const [delModal, setDelModal] = useState(false);
  const [isBiometricsSupport, setIsBiometricsSupport] = useState(false);
  const [isBiometricsActive, setIsBiometricsActive] = useState(false);
  const { requestDataDeletion } = useStore(accountStore);
  const navigate = useNavigate();

  useBlockScrollbar(bioModal || delModal, [bioModal, delModal]);

  useEffect(() => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === "ios" || platform === "android") {
        // BiometricAuth.checkBiometry()
        NativeBiometric.isAvailable({
          useFallback: false,
        })
          .then((info) => {
            // alert(JSON.stringify(info, null, 4));
            setIsBiometricsSupport(info.isAvailable);
          })
          .catch((err) => {
            console.error("Error checking authenticator availability", err);
          });
      } else {
        const isBrowserSupported = browserSupportsWebAuthn();
        if (isBrowserSupported) {
          const isWindows = navigator.userAgent.includes("Windows");
          if (isWindows) return;

          PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
            .then((available) => {
              setIsBiometricsSupport(available);
            })
            .catch((err) => {
              console.error("Error checking authenticator availability", err);
            });
        }
      }
    } catch (error) {}
  }, []);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    if (platform === "ios" || platform === "android") {
      NativeBiometric.getCredentials({
        server: window.location.hostname,
      })
        .then((value) => {
          if (value) {
            const [_, credId] = value.password.split("-");
            if (biometric === credId) {
              setIsBiometricsActive(true);
            }
          }
        })
        .catch(() => {});
    } else {
      setIsBiometricsActive(!!biometric);
    }
  }, [biometric]);

  const handleNavigate = () => navigate(PATH_LIST.account);
  const changePassword = () => navigate(PATH_LIST.changePassword);

  const handlePinCodeNavigation = () => {
    navigate(user?.hasPinCode ? PATH_LIST.changePIN : PATH_LIST.pinCode);
  };

  const requestToDelete = async (pinCode: string) => {
    setDelModal(false);
    try {
      await requestDataDeletion(err, pinCode);
    } catch {
      toast.error("Server Error");
    } finally {
      navigate(PATH_LIST.account);
    }
  };

  const verifyBio = () => {
    return new Promise<boolean>(async (res) => {
      try {
        await NativeBiometric.verifyIdentity({});
        res(true);
      } catch (error) {
        res(false);
      }
    });
  };

  const enableBiometrics = async (pinCode: string) => {
    setBioModal(false);
    try {
      const platform = Capacitor.getPlatform();
      let bioResp = null;
      if (platform === "ios" || platform === "android") {
        const verified = await verifyBio();
        if (!verified) {
          toast.error("Biometric registration failed");
          return;
        }
      } else {
        const response = await sircapApi.auth.biometricAuth({
          email: user!.email,
          entity,
        });

        if (!response.data || !response.data.data) {
          toast.error("Invalid biometric registration data");
          return;
        }

        bioResp = await startRegistration(response.data.data);
        if (!bioResp) {
          toast.error("Biometric registration failed");
          return;
        }
      }

      const resp = await sircapApi.auth.biometricAuthVerify({
        email: user!.email,
        entity,
        response: bioResp,
        platform,
        pinCode,
      });

      if (resp.error) {
        switch (resp.error.code) {
          case ResponseCodes.WrongPinCode:
            toast.error(err(String(resp.error.code)));
            break;

          default:
            break;
        }
      } else if (!resp.data || !resp.data.data) {
        toast.error("Biometric verification failed");
        return;
      }

      if (platform === "ios" || platform === "android") {
        await NativeBiometric.setCredentials({
          username: user!.email,
          password: `${entity}-${resp.data.data}`,
          server: window.location.hostname,
        });
      }

      sessionModel.sessionStore.setState({
        biometric: resp.data.data,
        biometrics: [...biometrics, resp.data.data],
      });
      setIsBiometricsActive(true);
    } catch (error) {
      // toast.error("Failed to start biometric registration");
    }
  };

  const disableBiometrics = async () => {
    try {
      const platform = Capacitor.getPlatform();
      let bioResp = null;
      if (platform === "ios" || platform === "android") {
        const verified = await verifyBio();
        if (!verified) {
          toast.error("Biometric verification failed");
          return;
        }
      } else {
        const response = await sircapApi.auth.biometricsOptions({});

        if (!response.data || !response.data.data) {
          toast.error("Invalid biometric registration data");
          return;
        }

        const options = response.data.data;
        bioResp = await startAuthentication(options);
      }

      const resp = await sircapApi.auth.removeBiometrics({
        platform,
        biometric_id: biometric,
        biometric_response: bioResp,
      });

      if (!resp.data || !resp.data.data) {
        toast.error("Biometric verification failed");
        return;
      } else {
        if (platform === "ios" || platform === "android") {
          await NativeBiometric.deleteCredentials({
            server: window.location.hostname,
          });
        }
      }

      sessionModel.sessionStore.setState({
        biometric: null,
        biometrics: biometrics.filter((v) => v !== biometric),
      });
      setIsBiometricsActive(false);
    } catch (error) {
      toast.error("Failed to remove biometrics");
    }
  };

  const securityOptions = [
    ...(isBiometricsSupport
      ? [
          {
            name: t("FaceIDFingerprint"),
            title: t("security").toUpperCase(),
            checkbox: true,
            checked: isBiometricsActive,
            onChange: !user?.hasPinCode
              ? () => setInactivePinCodeModal(true)
              : isBiometricsActive
              ? disableBiometrics
              : () => setBioModal(true),
            // notification: "new",
          },
        ]
      : []),
    {
      onEdit: changePassword,
      name: t("changePassword"),
      title: o("password").toUpperCase(),
      margin: isBiometricsSupport,
    },
    {
      onEdit: handlePinCodeNavigation,
      name: user?.hasPinCode ? t("changePinCode") : o("setPINCode"),
      notification: !user?.hasPinCode && "order",
    },
    {
      onEdit: () => setDelModal(true),
      name: t("delMyAcc"),
      deletion: true,
      title: t("securityOther").toUpperCase(),
      margin: true,
    },
  ];

  return (
    <div className="personal-info">
      <div className="personal-info-header">
        <img
          className="go-back-portfolio"
          onClick={handleNavigate}
          src={GoBack}
          alt="Go Back"
        />
        <p>{t("security")}</p>
      </div>

      <div className="personal-info-main">
        {securityOptions.map((options, index) => (
          <PersonalInfoEditCard
            key={index}
            onEdit={options.onEdit}
            name={options.name}
            security
            notification={
              options.notification === "new" || options.notification === "order"
                ? options.notification
                : false
            }
            deletion={options.deletion}
            label={{ title: options.title, margin: options.margin }}
            checkbox={{
              checkbox: options.checkbox,
              checked: options.checked,
              onChange: options.onChange,
            }}
          />
        ))}
      </div>

      {bioModal && (
        <Modal
          customModal="modalLogOut"
          onClick={enableBiometrics}
          onHide={() => {
            setBioModal(false);
          }}
          title={t("enableBiometric")}
          text="Biometrics"
          btnText={t("confirm")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          confirmDeal
          dealError={false}
        />
      )}

      {delModal && (
        <Modal
          customModal="modalLogOut"
          onClick={requestToDelete}
          onHide={() => setDelModal(false)}
          title={t("confirmAccDel")}
          text={t("proceedWithAccDel")}
          btnText={t("confirm")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          confirmDeal
        />
      )}
    </div>
  );
};
